import React from "react";
import { Icon } from "@iconify/react";
import { Link, useLocation } from "react-router-dom";
import { DASHBOARD, SOCIETY_CONTROLLER, SOCIETY_PORTAL } from "../Routing/routes";

// Map a page slug to an Iconify icon for visual consistency.
// Falls back to a generic icon when no match is found.
const ROUTE_ICON = {
  'society-management': 'solar:buildings-3-bold-duotone',
  'society-management/create': 'solar:buildings-3-bold-duotone',
  'sms-credit-request': 'solar:chat-round-line-bold-duotone',
  'sms-credit-package': 'solar:box-bold-duotone',
  'society-brand': 'solar:tag-bold-duotone',
  'society-location': 'solar:map-point-bold-duotone',
  'society-gatekeeper': 'solar:shield-user-bold-duotone',
  'society-admin-user': 'solar:user-id-bold-duotone',
  'society-manage-users': 'solar:users-group-rounded-bold-duotone',
  'society-manage-tenants': 'solar:user-rounded-bold-duotone',
  'society-manage-employees': 'solar:user-check-bold-duotone',
  'employee-types': 'solar:users-group-two-rounded-bold-duotone',
  'roles-permissions': 'solar:shield-keyhole-bold-duotone',
  'society-manage-vendors': 'solar:shop-2-bold-duotone',
};

const pickIcon = (pathname, headingTitle) => {
  if (!pathname) return 'solar:layers-bold-duotone';
  // Strip leading society-controller prefix
  const cleaned = pathname.replace(/^\/society-controller\/?/, '').split('/')[0];
  if (cleaned && ROUTE_ICON[cleaned]) return ROUTE_ICON[cleaned];
  // Heuristic by title
  const t = (headingTitle || '').toLowerCase();
  if (t.includes('community')) return 'solar:buildings-3-bold-duotone';
  if (t.includes('brand')) return 'solar:tag-bold-duotone';
  if (t.includes('vendor')) return 'solar:shop-2-bold-duotone';
  if (t.includes('tenant')) return 'solar:user-rounded-bold-duotone';
  if (t.includes('employee')) return 'solar:user-check-bold-duotone';
  if (t.includes('user')) return 'solar:users-group-rounded-bold-duotone';
  if (t.includes('admin')) return 'solar:user-id-bold-duotone';
  if (t.includes('sms') || t.includes('credit')) return 'solar:chat-round-line-bold-duotone';
  if (t.includes('location')) return 'solar:map-point-bold-duotone';
  if (t.includes('gatekeeper') || t.includes('login')) return 'solar:shield-user-bold-duotone';
  if (t.includes('settings')) return 'solar:settings-bold-duotone';
  return 'solar:layers-bold-duotone';
};

const Breadcrumb = ({
  headingTitle = 'Dashboard',
  title = 'Home',
  to,
  links = [],
  description = '',
  className = '',
  additionalStyle = {},
  replaceClass = '',
  icon,
  // Some pages sit under a menu group that has no page of its own ("Setup"),
  // so a trail starting with it led nowhere - the portal root bounced to login.
  // Those pages keep the heading and leave the trail off.
  showTrail = true,
}) => {
  const location = useLocation();
  const headerIcon = icon || pickIcon(location?.pathname, headingTitle);
  // Links stay inside the portal the page belongs to. Defaulting to the Society
  // Controller dashboard sent society-portal users (who can't open it) to login.
  const portalBase = location?.pathname?.startsWith(SOCIETY_PORTAL) ? SOCIETY_PORTAL : SOCIETY_CONTROLLER;
  const homeTo = to || portalBase + DASHBOARD;

  return (
    <div
      className={`tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4 ${className}`}
      style={{ ...additionalStyle }}
      data-replace={replaceClass}
    >
      {/* Title + icon */}
      <div className="tw-flex tw-items-center tw-gap-2.5">
        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-flex-shrink-0">
          <Icon icon={headerIcon} className="tw-text-white tw-text-base" />
        </div>
        <div>
          <h1 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-leading-tight tw-mb-0">
            {headingTitle}
          </h1>
          {description && (
            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5 tw-mb-0">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Breadcrumb trail */}
      {showTrail && (
      <ul className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-500 tw-flex-wrap tw-mb-0 tw-pl-0" style={{ listStyle: 'none' }}>
        <li>
          <Link to={homeTo} className="tw-flex tw-items-center tw-gap-1 hover:tw-text-rose-600 tw-transition-colors">
            <Icon icon="solar:home-smile-angle-outline" className="tw-text-sm" />
            <span className="tw-font-medium">{title}</span>
          </Link>
        </li>
        {links.map((item, index) => {
          const isLast = index === links.length - 1;
          return (
            <React.Fragment key={`${item?.text}-${index}`}>
              <li className="tw-text-gray-300">
                <Icon icon="mdi:chevron-right" className="tw-text-sm" />
              </li>
              <li className={`${isLast ? 'tw-text-rose-600 tw-font-medium' : 'tw-font-medium hover:tw-text-rose-600'}`}>
                {!isLast
                  ? <Link to={item?.to ? portalBase + item.to : '#'}>{item?.text}</Link>
                  : item?.text}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
      )}
    </div>
  );
};

export default Breadcrumb;
